import { Info } from "lucide-react";
import type { FraudAlertRow, ScoreData } from "../../types/tabs";
import {
  getFraudIdScanAlertRows,
  getFraudIdentityScanRows,
  getFraudSocialNumAlertRows,
  getFraudVictimIndicator,
  hasFraudAlerts,
} from "./creditReportTabLogic";

export function FraudAlertRowItem({ row }: { row: FraudAlertRow }) {
  return (
    <li className="text-xs leading-snug">
      {row.code != null && String(row.code) !== "" && (
        <span className="font-semibold text-amber-700">{row.code}</span>
      )}
      {row.description && (
        <span className="text-slate-700">
          {row.code != null && String(row.code) !== "" ? " — " : ""}
          {row.description}
        </span>
      )}
    </li>
  );
}

export function FraudAlertsSection({ title, rows }: { title: string; rows: FraudAlertRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-xs font-bold text-slate-800 mb-1">{title}</div>
      <ul className="ml-4 list-disc space-y-0.5">
        {rows.map((row, index) => (
          <FraudAlertRowItem key={index} row={row} />
        ))}
      </ul>
    </div>
  );
}

/** Fraud alerts hover popover — CSS group-hover, no JS state. */
export function FraudAlertsTooltip({ scoreData }: { scoreData: ScoreData }) {
  if (!hasFraudAlerts(scoreData)) return null;
  const victim = getFraudVictimIndicator(scoreData);
  const idScan = getFraudIdScanAlertRows(scoreData);
  const socialNum = getFraudSocialNumAlertRows(scoreData);
  const identity = getFraudIdentityScanRows(scoreData);

  return (
    <span className="group relative inline-block align-middle ml-1">
      <Info
        className="inline h-4 w-4 text-amber-500 cursor-pointer"
        aria-label="Fraud alerts"
        strokeWidth={2}
      />
      <div
        role="tooltip"
        className="invisible group-hover:visible absolute z-50 top-full left-1/2 -translate-x-1/2 mt-1 w-80 max-w-[20rem] rounded-md border border-slate-200 bg-white p-3 shadow-lg"
      >
        {victim && (
          <FraudAlertsSection title="Fraud victim indicator" rows={[victim]} />
        )}
        <FraudAlertsSection title="Fraud ID scan alert codes" rows={idScan} />
        <FraudAlertsSection title="Fraud social number alerts" rows={socialNum} />
        <FraudAlertsSection title="Identity scan" rows={identity} />
      </div>
    </span>
  );
}
